import { Reducer, AnyAction }           from 'redux';
import moment                           from 'moment';
import { MessagesState }                from '../../reduxTypes';
import {
    Message,
    Messages,
    MessageDeliveryStatuses,
    MessageActions
}                                       from '../../types';
import ActionTypes                      from '../../ActionTypes';
import { censorProfanity }              from '../../helpers/stringUtils';
import { removeDuplicatesByTemplateId } from '../../helpers/messageTemplate';

export const defaultState: MessagesState = {};

// Array.prototype.sort only expects number as return and parseInt(bool) is NaN :(
// return +1 if the item is greater, return -1 if it is less than
export const sortByDate = (a, b) => moment(a.date_added).isAfter(moment(b.date_added)) ? 1 : -1;

const getMessageList = (state: MessagesState, conversationId: number): Messages => state[`messages_${conversationId}`].messages;

const messagesAreEqual = (message: Message, newMessage: Message) => {
    return (message.templateId || message.content === newMessage.content) &&
    (message.delivery_status === MessageDeliveryStatuses.IN_PROGRESS || message.delivery_status === MessageDeliveryStatuses.ERROR) && // statuses indicate that message was not delivered
    message.action === MessageActions.SEND;
};

// Clean profanity in each message so that 'crap' becomes '****'
export const cleanMessageProfanity = message => {
    if (message.action === MessageActions.SEND) {
        return message;
    }

    return {
        ...message,
        content: censorProfanity(message.content)
    };
};

const MessagesReducer: Reducer<MessagesState> = (state: MessagesState = defaultState, action: AnyAction): MessagesState => {
    const total_count = action.total_count;

    switch (action.type) {
        case ActionTypes.MESSAGES_LOADED:
            // TODO: right now we are handling the re-sending of error message templates by filtering on the frontend
            // at some point we should refactor the messages feed service instead
            const validMessages = removeDuplicatesByTemplateId(action.messages)
                .sort(sortByDate);

            // Clean profanity in each message so that 'crap' becomes '****'
            const cleanMessages = validMessages.map(cleanMessageProfanity);

            return {
                ...state,
                [`messages_${action.conversationId}`] : {
                    messages: cleanMessages,
                    total_count: action.total_count
                }
            };

        case ActionTypes.MESSAGES_UPDATE:
            const updateLoadedState = getMessageList(state, action.conversationId);

            // remove any profanity and conditionally re-append the currentSession flag to show statuses on sent messages
            const cleanedNewMessages: Messages = action.messages.map(cleanMessageProfanity).map(item => (item.action === MessageActions.SEND ? {...item, currentSession: true} : item));

            // create a new array of unique messages from state by checking for matching IDs
            // create a new array including the newer messages
            // sort by date to ensure the messages are in the correct order
            const messages = updateLoadedState.filter(olderMessage => (
                cleanedNewMessages.reduce((hasUniqueId, newerMessage) => newerMessage.id === olderMessage.id ? false : hasUniqueId, true)
            )).concat(cleanedNewMessages).sort(sortByDate);

            return {
                ...state,
                [`messages_${action.conversationId}`] : {
                    ...state[`messages_${action.conversationId}`],
                    messages,
                    total_count
                }
            };

        case ActionTypes.MESSAGES_SEND_REQUEST:
            const newMessage = action.message;
            const optimisticMessages = getMessageList(state, action.conversationId)
                .filter((message: Message) => !messagesAreEqual(message, newMessage)) // TOOD: why are we filtering here?
                .concat(newMessage);

            const cleanedOptimisticMessages = optimisticMessages.map(message => cleanMessageProfanity(message)).sort(sortByDate);

            return {
                ...state,
                [`messages_${action.conversationId}`] : {
                    ...state[`messages_${action.conversationId}`],
                    messages: cleanedOptimisticMessages
                }
            };

        case ActionTypes.MESSAGES_SEND_SUCCESS:
            const successfulMessage = action.message;
            const successfulMessages = getMessageList(state, action.conversationId).map((message: Message) => {
                // check against action, status, and if the temporary ID matches what we set on the front end
                if (message.action === MessageActions.SEND && message.delivery_status === MessageDeliveryStatuses.IN_PROGRESS && message.id === successfulMessage.temporary_id) {
                    // if we get a match, replace the old message with the new message to get the real ID
                    return {
                        ...successfulMessage,
                        currentSession: true // we rely on this to conditionally show statuses
                    };
                }
                return message;
            }).sort(sortByDate);

            return {
                ...state,
                [`messages_${action.conversationId}`] : {
                    ...state[`messages_${action.conversationId}`],
                    messages: successfulMessages,
                    total_count
                }
            };

        case ActionTypes.MESSAGES_SEND_ERROR:
            const errorMessage = action.message;
            const errorMessages = getMessageList(state, action.conversationId).map((message: Message) => {
                    if (messagesAreEqual(message, errorMessage)) {
                        return {
                            ...message,
                            delivery_status: MessageDeliveryStatuses.ERROR
                        };
                    }
                    return message;
                });

            return {
                ...state,
                [`messages_${action.conversationId}`] : {
                    ...state[`messages_${action.conversationId}`],
                    messages: errorMessages
                }
            };

        default:
            return state;
    }
};

export default MessagesReducer;



// WEBPACK FOOTER //
// ./src/sections/MessagesFeed/MessagesReducer.ts